import moment from 'moment';

import { COIN_VALUES, COUNTRY, STATUS } from '../constants';
import { getCoinMechCapacities } from './machine/getCoinMechCapacities';

import { getLocalTime } from './time/time';
import {
	formatPercentage,
	generateUniqueId,
	formatCurrency,
	valueExists,
} from './utils';

import {
	DEFAULT_NEW_PLANOGRAM_ITEM,
	DEFAULT_NEW_INGREDIENT_PLANOGRAM_ITEM,
} from './vending-settings';

// --------------------------------------------------
// Utilities related specifically to vending machines
// --------------------------------------------------

// -------------------------------------------
// Utils - planograms
// -------------------------------------------

export function buildNewPlanogramItems (arg) {
	/*

	This function accepts an object argument, of this shape:

	{
		items: [
			{row: 1, column: 0},
			{row: 1, column: 1},
			{row: 1, column: 2},
		],
		planogramItems: optional array of planogram items,
		buildRow: optional object of shape: { row: 1, columns: 5 },
	}

	*/

	const {
		items,
		buildRow,
		planogramItems,
	} = arg;

	let newPlanogramItems = [];

	if (buildRow) {
		// If this option is passed in, build a whole row

		newPlanogramItems = [

			...new Array(buildRow.columns),

		].map((item, index) => {
			return (
				buildNewPlanogramItem({
					row: buildRow.row,
					column: index,
					planogramItems,
				})
			);
		});

		return newPlanogramItems;
	}

	items.forEach(item => {
		newPlanogramItems.push(buildNewPlanogramItem({
			row: item.row,
			column: item.column,
			planogramItems,
		}));
	});

	return newPlanogramItems;
}

export function buildNewPlanogramItem ({
	row, 			// Required
	column, 		// Required
	planogramItems, // Optional
	isIngredient,	// Optional
	planogramItemGroupId, // Optional
}) {
	/*

	This function builds a new planogram item at the row and column passed in as arguments.

	It is used in the PlanogramCreator and in PlanogramDetail, when adding new planogram items.

	It assigns a unique ID in planogramItems are passed in;
	IDs are required for editing functionality in PlanogramCreator.
	If planogramItems are not passed in, no ID is added, as this is
	done by the back-end.

	*/

	let newPlanogramItem = {
		...(isIngredient ? DEFAULT_NEW_INGREDIENT_PLANOGRAM_ITEM : DEFAULT_NEW_PLANOGRAM_ITEM),
		row,
		column,
		selection: isIngredient ? `${column + 1}` : `${row}${column}`,
		isIngredient,
		...(planogramItemGroupId ? { planogramItemGroupId } : {}),
	};

	// If planogramItems is passed in, generate a unique ID.
	// (Always done in PlanogramCreator)
	if (planogramItems) {
		newPlanogramItem = {
			...newPlanogramItem,
			id: generateUniqueId(planogramItems),
		};
	}

	return newPlanogramItem;
}


export function sortPlanogramItems (planogramItems, filterDisabled) {
	// This function sorts planogram items by selection
	// Optionally, it filters disabled selections

	// NOTE! Make sure you don't pass in planogramItems from props, as you
	// can't mutate that! Clone it first, with [... planogram.items]

	// check if any items
	if (!planogramItems || planogramItems.length === 0) {
		return planogramItems;
	}

	// Important: if row and column are not present on planogram items, simply return planogram items.
	// This is not relevant in most applications of this function.
	// It exists to handle, for example, when importing, importing Seed planograms, and applyPlanogram is used.
	// In these cases, we want to wrap planogram items, which invokes this function, but since there are no
	// rows and columns, it basically scrambles the planogram order. Planogram order comes in from Seed correct,
	// so we want to avoid this. (It otherwise makes sense, because without row and columns, what comes after this
	// if block makes no sense to perform anyway.
	if ((planogramItems[0].row === undefined) && (planogramItems[0].column === undefined)) {
		return planogramItems;
	}

	function compareFunction (a, b) {
		// If a's row is less than b's row, put a first
		if (a.row < b.row) {
			return -1;
		}
		// If a's row is greater than b's row, put b first
		if (a.row > b.row) {
			return 1;
		}
		// If a's row is the same as b's row

		// If a's column is less than b's column, put a first
		if (a.column < b.column) {
			return -1;
		}
		// If a's column is greater than b's column, put b first
		if (a.column > b.column) {
			return 1;
		}
		// This should never trigger as all planogram selections have unique
		// row and column combinations
		return 0;
	}

	const sortedPlanogramItems = [...planogramItems].sort(compareFunction);

	let filteredPlanogramItems = sortedPlanogramItems;
	if (filterDisabled) {
		filteredPlanogramItems = filteredPlanogramItems.filter(item => {
			return item.enabled;
		});
	}

	return filteredPlanogramItems;
}

export function numberOfItemsInRow (planogramItems, row) {
	// Helper function that determines how many planogram items are in its row
	// Used in determining whether to offset a whole row, and used in slightly
	// different variations in PlanogramCreator and PlanogramDetail

	const numberOfItemsInRow = planogramItems.reduce((acc, item) => {
		if (item.row === row && item.destroyed !== true) {
			return acc + 1;
		}
		return acc;
	}, 0);

	return numberOfItemsInRow;
}

export function numberOfRows (planogramItems) {
	// Helper function that calculates the number of rows in a planogram

	let numberOfRows;

	if (planogramItems.length === 0) {
		// Handle the case of no planogram items, else the below returns
		// Infinity rows.
		numberOfRows = 0;
	}
	else {
		numberOfRows = Math.max(...planogramItems.map(item => item.row));
	}

	return numberOfRows;
}

// -------------------------------------------
// Utils - vending machines
// -------------------------------------------


export function calculateDSL ({ previousDate, currentDate, context }) {
	/*

		This function expects a date string, and calculates
		days between that date and now.

	*/

	// If any of the dates doesn't exist
	if (!previousDate || !currentDate) {
		return null;
	}

	// Convert date object to moment object
	const momentPreviousDate = getLocalTime(context.tenant.timezone, previousDate).set({ hour: 12 }).startOf('hours');
	// Set now
	const momentCurrentDate = getLocalTime(context.tenant.timezone, currentDate).set({ hour: 12 }).startOf('hours');

	// Calculate day difference - "days since last ..."
	const dsl = momentCurrentDate.diff(momentPreviousDate, 'days');

	return dsl;
}

export function calculateDaysBetweenServices (lastService, currentService) {
	if (!lastService || !currentService) {
		return 'n/a';
	}

	const momentLastService = moment(lastService);
	const momentCurrentService = moment(currentService);

	const daysBetweenServices = momentCurrentService.diff(momentLastService, 'days');

	return daysBetweenServices;
}

export function calculateVendouts (planogramItems) {
	// This function expects an array of planogram item objects,
	// such as machine.planogram.items.

	// NOTE! If your query does not request field "enabled"
	// on planogram items, this isn't going to work, and will return
	// vendouts of 0! (since there are no enabled selections)

	const vendouts = planogramItems
		// Don't get disabled dex selections
		.filter(item => (item.enabled === true && item.inactive !== true))
		// Get selections that have quantity 0 or less to be safer than just 0.
		.filter(item => item.quantity < 1)
		.length;

	return vendouts;
}

export function calculatePercentageFull (planogramItems) {
	// This function expects an array of planogram item objects,
	// such as machine.planogram.items.

	// First filter enabled selections
	const enabledPlanogramItems = planogramItems.filter(item => item.enabled);

	// Get quantities in an array and reduce it to a total
	const aggregateQuantity = enabledPlanogramItems.reduce((acc, item) => {
		// Skips the item if it has no quantity
		if (item.quantity) {
			acc += item.quantity;
		}

		return acc;
	}, 0);

	// Get parlevels in an array and reduce it to a total
	const aggregateParlevel = enabledPlanogramItems.reduce((acc, item) => {
		// Skips the item if it has no parlevel
		if (item.parlevel) {
			acc += item.parlevel;
		}

		return acc;
	}, 0);

	// Return null in case of parlevel being 0. This will be the case where,
	// for example, a new machine is set up with no parlevels yet specified.
	if (aggregateParlevel === 0) {
		return null;
	}

	// Calculate percent full
	const percent = (aggregateQuantity / aggregateParlevel) * 100;

	// Round it
	const percentageFull = percent.toFixed(0);

	// Don't stringify it, so that sorting by this value is easier.

	return percentageFull;
}

// NO LONGER USED
// - Need to compute some of these for sorting;
// - Otherwise better to resolve with GraphQL for non-duplication


// export function enhanceMachines (machines) {

// 	// This function enhances an array of machines
// 	// by adding certain computed values.

// 	const enhancedMachines = machines.map(item => {
// 		return {
// 			...item,
// 			dsls: calculateDSLS(item.lastService),
// 			vendouts: item.planogram ? calculateVendouts(item.planogram.items) : null,
// 			percentageFull: item.planogram ? calculatePercentageFull(item.planogram.items) : null,
// 		};
// 	});
// 	return enhancedMachines;
// }


// --------------------------------------------------
// Utils - Service runs
// --------------------------------------------------

export const commQualities = {
	GOOD: 1,
	OKAY: 2,
	BAD: 3,
};

export function getLastCommQuality ({ communication: lastCommunication, minThreshold, maxThreshold }) {
	// This function calculates whether the last communication is good, okay, or bad

	const defaultGoodLimit = 24;
	const defultOkayLimit = 48;

	const goodLimit = minThreshold === undefined ? defaultGoodLimit : minThreshold;
	const okayLimit = maxThreshold === undefined ? defultOkayLimit : maxThreshold;

	const timeDifference = moment.utc().diff(lastCommunication, 'hours');

	if (timeDifference <= goodLimit) {
		return commQualities.GOOD;
	}
	if (timeDifference <= okayLimit) {
		return commQualities.OKAY;
	}

	return commQualities.BAD;
}

// Determine the status of a service run. It can be one of four types:
// [PLANNED, OPEN, IN PROGRESS, COMPLETED]

export const SERVICE_RUN_STATES = {
	PLANNED: 'Planned',				// Planned for a date in the future
	OPEN: 'Open',					// Active but no machines serviced
	IN_PROGRESS: 'In progress', 	// At least one machine has been serviced
	COMPLETED: 'Completed',			// All machines have been serviced
};

export function serviceRunStatus (serviceRun) {
	if (!serviceRun) {
		return false;
	}

	const { serviceDockets: dockets } = serviceRun;

	const allDocketsComplete = (dockets.length > 0) && dockets.every(docket => docket.status === STATUS.COMPLETE);
	if (allDocketsComplete) {
		return SERVICE_RUN_STATES.COMPLETED;
	}

	const someDocketsComplete = dockets.some(docket => docket.status === STATUS.COMPLETE || docket.status === STATUS.IN_PROGRESS);
	if (someDocketsComplete) {
		return SERVICE_RUN_STATES.IN_PROGRESS;
	}

	if (moment.utc().isBefore(serviceRun.planned, 'day')) {
		return SERVICE_RUN_STATES.PLANNED;
	}

	if (moment.utc().isSameOrAfter(serviceRun.planned, 'day')) {
		return SERVICE_RUN_STATES.OPEN;
	}
}



export function numberOfCompletedMachines (serviceRun) {
	// Gets the number of completed machines a service run.
	// Requires service dockets and the completed field to be passed in
	// with the service run.

	// Return null if there are no service dockets.
	// This is a failsafe that should never trigger, as there should always be dockets.
	if (!serviceRun.serviceDockets) {
		return null;
	}

	const completedMachines = serviceRun.serviceDockets
		.filter(docket => docket.status === STATUS.COMPLETE)
		.length;

	return completedMachines;
}

export function combineMachineListWithDockets (machines, serviceDockets) {
	// Enhances a machine list by looking up docket information in a service
	// dockets array.

	const updatedMachines = machines.map(machine => {
		const updatedMachine = { ...machine };

		serviceDockets.forEach(docket => {
			if (docket.machine.id === machine.id) {
				updatedMachine.status = docket.status;
				updatedMachine.date = docket.date;
				updatedMachine.manualRestock = docket.manualRestock;
				updatedMachine.inventoryCheck = docket.inventoryCheck;
				updatedMachine.location = docket.machine && docket.machine.location;
				updatedMachine.docketId = docket.id;
			}
		});
		return updatedMachine;
	});

	return updatedMachines;
}


// --------------------------------------------------
// Utils - Coin mechs
// --------------------------------------------------

export function getTubeDetails (tube, coinMechCapacities, coinMechHasCapacities, tenantSettings, coinMechLimits) {
	// Gets coin mech tube information for a single tube
	// if coinLimit exists adjust values to the coin limit
	const coinValue = formatCurrency(tube.valueCoinsInTube);
	const { numberCoinsInTube } = tube;
	const capacity = coinMechCapacities[tube.valueCoinsInTube];
	const coinLimit = coinMechLimits[tube.valueCoinsInTube];

	const percentFull = formatPercentage(parseInt(tube.numberCoinsInTube, 10) / parseInt(coinLimit || capacity, 10));
	const percentFullAsInt = (parseInt(tube.numberCoinsInTube, 10) / parseInt(coinLimit || capacity, 10)) * 100;
	const percentFullToCapacityAsInt = (parseInt(tube.numberCoinsInTube, 10) / parseInt(capacity, 10)) * 100;
	const percentEmptyAsInt = 100 - percentFullToCapacityAsInt;

	const requiresFill = coinMechHasCapacities
		? percentFullAsInt < tenantSettings.requiredCoinsPercentageThresholds[0]
		: numberCoinsInTube < tenantSettings.requiredCoinsThresholds[0] && COIN_VALUES[COUNTRY.AUSTRALIA].includes(parseInt(tube.valueCoinsInTube, 10));
		// we can get information about 5c capacity from dex, but don't want rise "coinsRequired"
		// cause Australia hasn't 5c coins

	const numberOfCoinsRequired = coinMechHasCapacities
		? (coinLimit || capacity) - parseInt(tube.numberCoinsInTube, 10)
		: null;

	const tubeDetails = {
		coinValue,
		numberCoinsInTube,
		capacity,
		coinLimit,
		percentFull,
		percentFullAsInt,
		percentEmptyAsInt,
		requiresFill,
		numberOfCoinsRequired,
		percentFullToCapacityAsInt,
		coinMechHasCapacities,
		requiredCoinsThresholds: tenantSettings.requiredCoinsThresholds,
		requiredCoinsPercentageThresholds: tenantSettings.requiredCoinsPercentageThresholds,
	};

	return tubeDetails;
}

export function analyseCoinMech (tubesDetail, machineCoinMech, tenantSettings, machine) {
	// If tubesDetail does not exist (e.g. if the machine
	// is an offline machine, or has not yet been set up, or has
	// not dialled in, etc), specify this in the return object
	if (!tubesDetail?.length) {
		return {
			noCoinMechData: true,
			label: 'No coin mech data',
		};
	}

	const coinMechHasCapacities = valueExists(machineCoinMech?.settings?.coinMechCapacities);

	// get coins values declared in tenantConstant.settings.coinMechCapacities (if exists)
	if (coinMechHasCapacities) {
		tubesDetail = tubesDetail.filter(tube => machineCoinMech?.settings?.coinMechCapacities[tube.valueCoinsInTube]);
	}

	const coinMechCapacities = getCoinMechCapacities(machineCoinMech, tubesDetail);

	const coinMechLimits = Object.keys(coinMechCapacities || {}).reduce((acc, coinValue) => {
		acc[coinValue] = machine.overrideCoinLimits ? machine.coinLimits?.[coinValue] : machineCoinMech?.settings?.coinMechLimits?.[coinValue];
		return acc;
	}, {});

	// add values declared in coinMech but not existed in most recent dex
	Object.entries(coinMechCapacities).forEach(([coinMechValue, capacity]) => {
		let increaseCoinTypeNumber = 1; // to not to duplicate coinTypeNumber
		if (capacity > 0 && !tubesDetail.some(tubeDetail => tubeDetail.valueCoinsInTube === coinMechValue)) {
			tubesDetail.push({
				coinTypeNumber: (10 + increaseCoinTypeNumber).toString(),
				numberCoinsInTube: '0',
				tubeIsFull: null,
				valueCoinsInTube: coinMechValue.toString(),
			});
			increaseCoinTypeNumber += 1;
		}
	});

	// sort by coinValue
	tubesDetail = [...tubesDetail].sort((a, b) => parseInt(a.valueCoinsInTube, 10) -  parseInt(b.valueCoinsInTube, 10));

	// Enhances tubeDetails array from dex by calling
	// getTubeDetails on each tube, and providing some additional analysis
	// on the coin mech.
	const analysedTubes = tubesDetail.map(tube => getTubeDetails(tube, coinMechCapacities, coinMechHasCapacities, tenantSettings, coinMechLimits));

	const mechRequiresFill = analysedTubes.some(tube => {
		// If any single tube requires filling, then this returns
		// true (the coin mech requires filling)
		return tube.requiresFill;
	});

	const requiredCoins = analysedTubes.filter(tube => tube.requiresFill).map(tube => tube.coinValue);

	let label = 'Does not require fill';
	if (mechRequiresFill) {
		label = `Required coins: ${requiredCoins.join(', ')}`;
	}

	return {
		coinMechHasCapacities,
		analysedTubes,
		mechRequiresFill,
		requiredCoins,
		label,
		noCoinMechData: false,
	};
}

export const setTubesTimeSeries = (reversedData, machineCoinMech) => {

	const usedCoinValues = []; // needed to find unexpected coinValues
	const coinMechCapacities = machineCoinMech?.settings?.coinMechCapacities;

	// get the greates number of coinValues from dex
	const { greatestNumberOfTubes } = (reversedData || []).reduce((acc, data) => {
		acc.greatestNumberOfTubes = data.tubesDetail?.length > acc.greatestNumberOfTubes ? data.tubesDetail?.length : acc.greatestNumberOfTubes;
		return acc;
	}, { greatestNumberOfTubes: 0 });

	const totalValuesInTubes = [];

	for (let i = 0; i < greatestNumberOfTubes; i++) {
		let tubeCoin = reversedData[0]?.tubesDetail[i]?.valueCoinsInTube;

		// Continue if no coin
		if (!tubeCoin) {
			// find coinValue defined in coinMech but not existed in latest dex
			reversedData.some((item) => {
				if (item.tubesDetail[i]?.valueCoinsInTube && !usedCoinValues.includes(item.tubesDetail[i]?.valueCoinsInTube)) {
					tubeCoin = item.tubesDetail[i]?.valueCoinsInTube;
				}
				// we can sometimes receive dex with "strange value"
				// find it and display in chart
				else {
					const tubeDetail = item.tubesDetail?.find((tubeDetail) => !usedCoinValues.includes(tubeDetail.valueCoinsInTube));
					tubeCoin = tubeDetail?.valueCoinsInTube;
				}
				return tubeCoin;
			});
		}

		usedCoinValues.push(tubeCoin);
		// if 'unexpected value' isn't declared in coinMech do not display it
		if (!tubeCoin || coinMechCapacities && !coinMechCapacities[tubeCoin]) {
			continue;
		}

		const tube = reversedData.map(item => {
			if (!item.tubesDetail) {
				return null;
			}

			const tubeDetailData = item.tubesDetail.find(tubeDetail => {
				return tubeDetail.valueCoinsInTube === tubeCoin;
			});
			// Lots of &&s in case coin mech data has discrepancies
			return tubeDetailData && tubeDetailData.numberCoinsInTube;
		});
		totalValuesInTubes.push({
			label: formatCurrency(tubeCoin),
			data: tube,
			yAxisID: 'y',
			fill: false,
		});
	}

	// sort data to display it pretty on chart
	totalValuesInTubes.sort((a, b) => parseInt(a.label.substring(1), 10) -  parseInt(b.label.substring(1), 10));

	return ({
		datasets: totalValuesInTubes,
		heading: 'Historical coin mech data',
		labelString: 'Number of coins in tube',
	});
};

export const setTotalValueSeries = (reversedData) => {

	const totalValues = reversedData.map(item => {
		if (!item.tubesValueTotal) {
			return null;
		}

		return item.tubesValueTotal;
	});

	return (
		{
			datasets: [{
				label: 'Cash total',
				data: totalValues,
				yAxisID: 'y',
				fill: false,
			}],
			heading: 'Historical coin mech data',
			labelString: 'Total value of coins in machine',
		});
};
